/**
 * Pure helpers for the Super Admin outreach email editor (Gautam/components/OutreachEmailEditor.vue).
 * Plan: teamio-serverless-api/docs/plans/2026-10-09-customer-lifecycle-phase1-plan.md, sections 15 and 16.
 *
 * Nothing here talks to the network and nothing here decides whether an email may go out: the SERVER validates the
 * words, renders the email, and answers "would this practice qualify" (lifecycle-service/lib/outreachEligibility.js).
 * These helpers only turn the server's answers into the sentences and states the screen shows.
 *
 * The insert-at-the-cursor splice for the variable chips is NOT here: it is the existing src/utils/textInsert.js.
 *
 * Keep IMPORT-FREE: cypress/node-tests/outreach-editor.test.js executes this exact file.
 */

/** Wait this long after the last keystroke before asking the server for a new preview. */
export const PREVIEW_DELAY_MS = 400;

/**
 * Which email fits this practice's row from the Customer Health report. Mirrors each template's `appliesTo` on the
 * server (lifecycle-service/lib/outreachTemplates.js); the node-test checks the two agree against the real evaluator.
 * It only chooses what the screen opens on: the server's preview still says whether the practice qualifies.
 */
export function suggestTemplateKey(site) {
  if (!site || typeof site !== 'object') return null;
  if (site.status === 'at_risk') return 'quiet_stretch';
  if (site.status === 'onboarding' && site.onboardingStage === 'slow') return 'onboarding_slow';
  if (site.status === 'onboarding' && site.onboardingStage === 'stalled') return 'onboarding_stalled';
  return null;
}

/** The words the boxes start with: the latest saved version, or the starting text when nothing is saved yet. */
export function startingDraft(template) {
  const from = template && template.latest ? template.latest : (template && template.starting) || {};
  return {
    subject: typeof from.subject === 'string' ? from.subject : '',
    body: typeof from.body === 'string' ? from.body : '',
  };
}

export function sameWords(a, b) {
  return !!a && !!b && a.subject === b.subject && a.body === b.body;
}

/** "12 / 5000" under a box. */
export function countText(text, max) {
  const used = typeof text === 'string' ? text.length : 0;
  return Number.isInteger(max) ? `${used} / ${max}` : String(used);
}

/** "Version 2 saved on Oct 9, 2026." or the not-saved-yet sentence. `dayText` is the already formatted date. */
export function savedLine(latest, dayText) {
  if (!latest || !Number.isInteger(latest.version)) return 'Not saved yet. These are the starting words.';
  return dayText && dayText !== '—' ? `Version ${latest.version} saved on ${dayText}.` : `Version ${latest.version} is saved.`;
}

/**
 * The server returns a COUNT of the practice's Admins, never their addresses. null means it could not count (never
 * read that as zero); 0 means nobody at the practice has an address on file.
 */
export function recipientsLine(recipients) {
  if (!recipients || typeof recipients !== 'object') return null;
  const n = recipients.count;
  if (n === null || n === undefined || !Number.isInteger(n) || n < 0) {
    return { tone: 'warn', text: "We couldn't count who would get this. Press Check again." };
  }
  if (n === 0) return { tone: 'warn', text: "No Admin at this practice has an email address on file, so this email would reach no one." };
  return { tone: 'ok', text: n === 1 ? "1 person (the practice's Admin) would get this email." : `${n} people (the practice's Admins) would get this email.` };
}

/** The server's answer to "would this practice qualify right now", as a headline and the server's own reasons. */
export function eligibilityView(eligibility) {
  if (!eligibility || typeof eligibility !== 'object') return null;
  if (eligibility.allowed === true) return { tone: 'ok', headline: 'This practice qualifies for this email today.', reasons: [] };
  const reasons = (Array.isArray(eligibility.blockers) ? eligibility.blockers : [])
    .map((b) => (b && typeof b.message === 'string' ? b.message.trim() : ''))
    .filter(Boolean);
  return { tone: 'warn', headline: 'This email would not go to this practice today.', reasons };
}

/**
 * The audiences the Send button offers (plan section 21). NOTHING is preselected: the choice starts as null, and the
 * only values that count are these two exact words, which are also exactly what the server accepts.
 */
export const AUDIENCE = Object.freeze({ ME: 'me', PRACTICE: 'practice' });

/** The audience if (and only if) it is exactly one of the two words; otherwise null ("nothing chosen"). */
export function audienceChoice(value) {
  return value === AUDIENCE.ME || value === AUDIENCE.PRACTICE ? value : null;
}

/**
 * The server's answer to "send me a test". Only an answer that says `sent: true` AND names the address it went to is a
 * success the screen may announce; anything else is treated as "we cannot tell", never as "sent".
 */
export function testSentLine(data) {
  if (!data || data.sent !== true || typeof data.to !== 'string' || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.to.trim())) return null;
  return `Test sent to ${data.to.trim()}. Nothing went to the practice.`;
}

/**
 * Whether the screen may offer "email the practice's Admins" for THESE words, and if not, the plain reasons why.
 * It mirrors what the server re-checks on its own (lifecycle-service sendToPractice), so the button is only enabled when
 * the server has any chance of accepting; the server stays the gate.
 *
 *   preview   the server's last answer for THESE words: { state: 'ready'|..., data, signature }
 *   signature the signature of the words in the boxes right now (a preview of older words does not count)
 *   latest    the saved version the screen knows: { version, subject, body } or null
 *   draft     the words in the boxes: { subject, body }
 *
 * The practice receives the SAVED words, never the boxes, so unsaved edits (or never-saved words) are a reason to stop.
 * Returns { ok, reasons, count, version } where count and version are what the person is asked to confirm.
 */
export function practiceReadiness({ preview, signature, latest, draft } = {}) {
  const reasons = [];
  const data = preview && preview.state === 'ready' ? preview.data : null;
  if (!data || preview.signature !== signature) {
    reasons.push('Checking these words. Press Check again if this stays.');
    return { ok: false, reasons, count: null, version: null };
  }
  if (!latest || !Number.isInteger(latest.version)) {
    reasons.push('These words are not saved yet. The practice gets the saved words, so save them first.');
  } else if (!sameWords(draft, latest)) {
    reasons.push('You have changes that are not saved. The practice gets the saved words, so save your changes first.');
  } else if (data.savedVersion !== latest.version) {
    reasons.push('The check below is for a different version. Press Check again.');
  }
  if (!data.validation || data.validation.ok !== true) reasons.push('The server did not accept these words.');
  if (!data.rendered || data.renderError) reasons.push('This email cannot be made for this practice.');
  const eligibility = eligibilityView(data.eligibility);
  if (!eligibility || eligibility.tone !== 'ok') {
    if (eligibility && eligibility.reasons.length) reasons.push(...eligibility.reasons);
    else reasons.push('This email would not go to this practice today.');
  }
  const count = data.recipients && Number.isInteger(data.recipients.count) ? data.recipients.count : null;
  if (count === null) reasons.push("We couldn't count who would get this. Press Check again.");
  else if (count < 1) reasons.push('No Admin at this practice has an email address on file, so this email would reach no one.');
  const version = latest && Number.isInteger(latest.version) ? latest.version : null;
  return { ok: reasons.length === 0, reasons, count: reasons.length === 0 ? count : null, version: reasons.length === 0 ? version : null };
}

/** The sentence on the confirm step: the exact number, the practice and the saved version. */
export function practiceConfirmLine(siteName, count, version) {
  const who = count === 1 ? '1 person' : `${count} people`;
  return `This will email ${who} at ${siteName || 'this practice'} (the practice's Admins), using saved version ${version}. It cannot be taken back.`;
}

/**
 * The server's answer to "email the practice". Only `sent: true`, for the practice, with a whole number of people, is a
 * success the screen may announce; anything else is "we cannot tell", never "sent".
 */
export function practiceSentLine(data, siteName) {
  if (!data || data.sent !== true || data.audience !== AUDIENCE.PRACTICE || !Number.isInteger(data.count) || data.count < 1) return null;
  const who = data.count === 1 ? '1 person' : `${data.count} people`;
  return `Sent to ${who} at ${siteName || 'the practice'}.`;
}

/**
 * A failed "send me a test", sorted by what the screen should do:
 *   invalid          the server refused the words and listed every problem
 *   unknown_outcome  the email provider did not confirm: it MAY have gone out (a warning, not an error)
 *   other            anything else (not signed in, not allowed, over the limit, the provider refused it, no response):
 *                    the caller uses its generic message helper, which shows the server's own sentence
 */
export function testFailure(err) {
  const asSave = saveFailure(err);
  if (asSave.kind === 'invalid') return asSave;
  const response = err && err.response;
  const data = response && response.data;
  if (response && response.status === 502 && data && typeof data === 'object' && data.code === 'outcome_unknown'
    && typeof data.error === 'string' && data.error.trim()) {
    return { kind: 'unknown_outcome', messages: [data.error.trim()] };
  }
  return { kind: 'other', messages: [] };
}

/**
 * A failed Save, sorted by what the screen should do about it:
 *   invalid   the server refused the words and listed every problem; show them all
 *   conflict  someone saved a newer version first; show the sentence and re-read what is saved
 *   other     anything else (no response, 401/403, 500): the caller uses its generic message helper
 */
export function saveFailure(err) {
  const response = err && err.response;
  const data = response && response.data;
  if (response && data && typeof data === 'object') {
    if (response.status === 400 && data.code === 'invalid_draft' && Array.isArray(data.problems)) {
      const messages = data.problems.map((p) => (p && typeof p.message === 'string' ? p.message.trim() : '')).filter(Boolean);
      if (messages.length) return { kind: 'invalid', messages };
    }
    if (response.status === 409 && data.code === 'conflict' && typeof data.error === 'string' && data.error.trim()) {
      return { kind: 'conflict', messages: [data.error.trim()] };
    }
  }
  return { kind: 'other', messages: [] };
}
